import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Sparkles, X, Send, Loader2, RotateCcw, ChevronDown, Lock, Key } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import ChatMessageRenderer from "@/components/ChatMessageRenderer";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const VIEWPORT_MARGIN = 16;

const DEFAULT_SUGGESTIONS = [
  "What was yesterday's production report?",
  "What was today's production report?",
  "What were yesterday's goods receipts?",
];

export default function MesproAIFloatingWidget() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isConfigured, setIsConfigured] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I am **Mespro AI**, your intelligent assistant for SAP production planning, process orders, and inventory analytics. How can I help you today?",
      timestamp: new Date().toISOString(),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState(DEFAULT_SUGGESTIONS);
  const [position, setPosition] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);
  const dragRef = useRef(null);
  const ignoreClickRef = useRef(false);

  const checkAIStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agent/status`);
      setIsConfigured(Boolean(res.data?.enabled ?? res.data?.has_key));
    } catch {
      try {
        const cfg = await axios.get(`${API}/settings/ai-config`);
        setIsConfigured(Boolean(cfg.data?.has_key));
      } catch {
        setIsConfigured(false);
      }
    }
  }, []);

  useEffect(() => {
    checkAIStatus();

    const handleConfigChange = (e) => {
      if (e?.detail?.has_key !== undefined) {
        setIsConfigured(Boolean(e.detail.has_key));
      } else {
        checkAIStatus();
      }
    };

    window.addEventListener("ai-config-changed", handleConfigChange);
    return () => window.removeEventListener("ai-config-changed", handleConfigChange);
  }, [checkAIStatus]);

  const clampPosition = (left, top) => {
    const launcher = launcherRef.current;
    const width = launcher?.offsetWidth || 150;
    const height = launcher?.offsetHeight || 48;
    return {
      left: Math.min(Math.max(VIEWPORT_MARGIN, left), window.innerWidth - width - VIEWPORT_MARGIN),
      top: Math.min(Math.max(VIEWPORT_MARGIN, top), window.innerHeight - height - VIEWPORT_MARGIN),
    };
  };

  useEffect(() => {
    const keepLauncherVisible = () => {
      setPosition((current) => current && clampPosition(current.left, current.top));
    };
    window.addEventListener("resize", keepLauncherVisible);
    return () => window.removeEventListener("resize", keepLauncherVisible);
  }, []);

  useEffect(() => {
    if (isOpen) {
      checkAIStatus();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, checkAIStatus]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = async (textToSend = input) => {
    const query = textToSend.trim();
    if (!query || loading) return;

    if (isConfigured === false) {
      toast.error("Mespro AI is inactive. Please configure an API key in AI Configuration.");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "🔒 **Mespro AI is inactive.**\n\nAn active API key is required to use Mespro AI. Please configure and test an API key under **Settings > AI Configuration**.",
          timestamp: new Date().toISOString(),
        },
      ]);
      return;
    }

    const userMessage = {
      role: "user",
      content: query,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const endpoint = `${API}/agent/chat`;
      const res = await axios.post(endpoint, {
        message: query,
        session_id: sessionId,
        context: { current_url: window.location.pathname },
      });

      if (res.data?.data?.error === "api_key_required" || res.data?.data?.configured === false) {
        setIsConfigured(false);
      }

      const reply = res.data.response || res.data.message || "Request analyzed.";
      if (res.data.session_id) setSessionId(res.data.session_id);
      if (res.data.suggestions?.length) setSuggestions(res.data.suggestions);

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: reply,
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      console.error("AI Chat Error:", err);
      const detail = err.response?.data?.detail;
      const fallbackReply = detail
        ? `⚠️ **Mespro AI Notice:** ${detail}`
        : "Mespro AI could not complete this request. Please try again.";
      
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: fallbackReply,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setMessages([
      {
        role: "assistant",
        content: "Conversation reset. What would you like to ask Mespro AI?",
        timestamp: new Date().toISOString(),
      },
    ]);
    setSessionId(null);
    setSuggestions(DEFAULT_SUGGESTIONS);
    toast.success("Chat history cleared");
  };

  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const bounds = launcherRef.current?.getBoundingClientRect();
    if (!bounds) return;

    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      left: bounds.left,
      top: bounds.top,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const offsetX = event.clientX - drag.startX;
    const offsetY = event.clientY - drag.startY;
    if (Math.abs(offsetX) > 4 || Math.abs(offsetY) > 4) drag.moved = true;
    if (!drag.moved) return;

    event.preventDefault();
    setPosition(clampPosition(drag.left + offsetX, drag.top + offsetY));
  };

  const handlePointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    if (drag.moved) {
      const nextPosition = clampPosition(drag.left + event.clientX - drag.startX, drag.top + event.clientY - drag.startY);
      setPosition(nextPosition);
      ignoreClickRef.current = true;
    }
    dragRef.current = null;
  };

  const handleLauncherClick = () => {
    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const openChatBelow = Boolean(position && position.top < 540);

  return (
    <div
      ref={launcherRef}
      className="fixed bottom-6 right-6 z-50 flex flex-col items-end"
      style={position ? { left: position.left, top: position.top, right: "auto", bottom: "auto" } : undefined}
      data-testid="mespro-ai-floating"
    >
      {/* Floating Chat Drawer */}
      {isOpen && (
        <Card
          className={`absolute w-96 max-w-[calc(100vw-2rem)] h-[520px] max-h-[82vh] ${openChatBelow ? "top-[60px]" : "bottom-[60px]"} flex flex-col shadow-2xl border bg-card rounded-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-5 duration-200`}
          style={position && position.left < 260 ? { left: 0 } : { right: 0 }}
        >
          {/* Header */}
          <div className="p-3.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-primary text-white flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-xs flex items-center justify-center">
                {isConfigured === false ? <Lock className="w-4 h-4 text-amber-200" /> : <Sparkles className="w-4 h-4 text-white" />}
              </div>
              <div>
                <h3 className="font-semibold text-sm leading-tight flex items-center gap-1.5">
                  Mespro AI
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border-white/30 text-white font-normal ${isConfigured === false ? "bg-amber-500/30" : "bg-white/10"}`}>
                    {isConfigured === false ? "Inactive" : "Copilot"}
                  </Badge>
                </h3>
                <p className="text-[11px] text-blue-100/90 flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${isConfigured === false ? "bg-amber-300" : "bg-emerald-400 animate-pulse"}`}></span>
                  {isConfigured === false ? "API Key Required" : "SAP Business Assistant"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/10"
                onClick={handleClear}
                title="Clear conversation"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/10"
                onClick={() => setIsOpen(false)}
                title="Minimize chat"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3.5 space-y-3 bg-muted/20">
            {isConfigured === false && (
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-center space-y-2.5 shadow-2xs">
                <div className="w-9 h-9 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 mx-auto flex items-center justify-center">
                  <Lock className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-semibold text-xs text-foreground">Mespro AI Inactive</h4>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                    An active API key is required to use Mespro AI. Please configure and test an API key under AI Configuration to unlock AI assistance.
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/ai-config");
                  }}
                  className="h-7 text-xs px-3 bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 mx-auto"
                >
                  <Key className="w-3 h-3" />
                  Configure AI Key
                </Button>
              </div>
            )}

            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-2 text-xs ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "assistant" && (
                  <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="w-3.5 h-3.5" />
                  </div>
                )}
                <div
                  className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-xs shadow-2xs leading-relaxed ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-xs"
                      : "bg-card border text-card-foreground rounded-bl-xs"
                  }`}
                >
                  <ChatMessageRenderer content={msg.content} />
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2 text-xs justify-start">
                <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="w-3.5 h-3.5 animate-pulse" />
                </div>
                <div className="bg-card border rounded-2xl rounded-bl-xs px-3.5 py-2.5 text-xs text-muted-foreground flex items-center gap-2 shadow-2xs">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                  <span>Mespro AI is thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Suggestion Chips - only show if configured */}
          {isConfigured !== false && suggestions.length > 0 && !loading && (
            <div className="px-3 py-2 bg-card border-t flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {suggestions.slice(0, 3).map((chip, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSend(chip)}
                  className="text-[11px] bg-muted hover:bg-primary/10 hover:text-primary transition-colors text-muted-foreground px-2.5 py-1 rounded-full border text-left truncate max-w-full"
                >
                  💡 {chip}
                </button>
              ))}
            </div>
          )}

          {/* Input Footer */}
          <div className="p-3 bg-card border-t">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex items-center gap-2"
            >
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isConfigured === false ? "Configure API key in AI Configuration to chat..." : "Ask Mespro AI anything..."}
                className="h-9 text-xs font-normal"
                disabled={loading || isConfigured === false}
              />
              <Button
                type="submit"
                size="icon"
                disabled={loading || isConfigured === false || !input.trim()}
                className="h-9 w-9 shrink-0"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </div>
        </Card>
      )}

      {/* Floating Action Trigger Button */}
      <Button
        onClick={handleLauncherClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className="h-12 px-4 rounded-full bg-gradient-to-r from-blue-600 via-indigo-600 to-primary text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 flex items-center gap-2 border border-white/20 touch-none cursor-grab active:cursor-grabbing"
        data-testid="toggle-mespro-ai-btn"
      >
        <div className="relative">
          {isConfigured === false ? <Lock className="w-4 h-4 text-amber-200" /> : <Sparkles className="w-5 h-5 animate-pulse" />}
          <span
            className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ring-2 ring-blue-600 ${
              isConfigured === false ? "bg-amber-400" : "bg-emerald-400"
            }`}
          ></span>
        </div>
        <span className="font-semibold text-xs tracking-wide">Mespro AI</span>
        {isOpen ? <ChevronDown className="w-4 h-4 ml-0.5" /> : null}
      </Button>
    </div>
  );
}
